// Walking calorie burn — an estimate that knows which of its inputs were
// measured and which were assumed, and says so.
//
// Pure functions only, same contract as liftingBurn.js and src/food/burn.js:
// no React, no network, no SharePoint — callers hand in plain values. Absence
// is not zero: a missing weight, a missing effort, or a walk with neither a
// distance nor a duration yields null, never a default. No `|| 0` anywhere.
//
// REUSE. restingCalPerDay is imported from src/food/burn.js — the Mifflin-St
// Jeor formula and its hardcoded height/age live there and ONLY there;
// duplicating them here would let the two drift on the next birthday. This is
// the first src/fitness -> src/food import, the mirror image of the crossing
// Food.js:27-38 documents in the other direction, and like that one it is a
// data crossing, not a visual one.

import { restingCalPerDay } from '../food/burn';

// Compendium walking METs by speed on level ground. Linear interpolation
// between anchors; flat-clamped outside them — the curve has no opinion about
// speeds it has no data for.
const SPEED_MET_ANCHORS = [
  [2.0, 2.8],
  [2.5, 3.0],
  [3.0, 3.5],
  [3.5, 4.3],
  [4.0, 5.0],
  [4.5, 7.0],
];

export function metForSpeed(mph) {
  if (typeof mph !== 'number' || !Number.isFinite(mph)) {
    throw new Error(`metForSpeed: expected a finite mph, got ${JSON.stringify(mph)}`);
  }
  const first = SPEED_MET_ANCHORS[0];
  const last = SPEED_MET_ANCHORS[SPEED_MET_ANCHORS.length - 1];
  if (mph <= first[0]) return first[1];
  if (mph >= last[0]) return last[1];
  for (let i = 1; i < SPEED_MET_ANCHORS.length; i++) {
    const [x1, y1] = SPEED_MET_ANCHORS[i];
    if (mph <= x1) {
      const [x0, y0] = SPEED_MET_ANCHORS[i - 1];
      return y0 + ((mph - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return last[1];
}

// What each effort claims about pace when no pace was measured. nominal is the
// point estimate; low/high are the band the assumed paths recompute at.
const ASSUMED_SPEEDS = {
  easy:   { nominal: 2.5, low: 2.2, high: 2.8 },
  steady: { nominal: 3.3, low: 3.0, high: 3.6 },
  hard:   { nominal: 4.0, low: 3.8, high: 4.5 },
};

// What each effort claims about grade and load when pace IS measured — see
// the Path A comment below for why these apply there and only there.
const GRADE_MULTIPLIERS = { easy: 1.0, steady: 1.15, hard: 1.45 };

const LBS_PER_KG = 2.2046;
const MINUTES_PER_DAY = 1440;

// Same rounding step as liftingBurn.js, for the same reason: every burn
// figure at this scale should read as the estimate it is.
export const WALK_BURN_ROUNDING = 10;

// Path A sanity bounds. A measured speed outside them is a data-entry error
// or not a walk; the estimate still computes (clamped to the curve's ends)
// but carries `suspect: true` so the UI can say so.
const SUSPECT_LOW_MPH = 1.0;
const SUSPECT_HIGH_MPH = 6.0;

// Net of resting, same shape as burn.js's swim block: gross MET kcal counts
// the whole cost of being alive for those minutes, and any daily figure this
// joins has already paid for them once. Floored at zero — a walk so short the
// resting subtraction exceeds its gross contributes nothing, not a credit.
function netWalkCal(minutes, met, kg) {
  const restingPerMin = restingCalPerDay(kg) / MINUTES_PER_DAY;
  const gross = minutes * met * 3.5 * kg / 200;
  const net = gross - (minutes * restingPerMin);
  return Math.max(0, net);
}

// { low, mid, high, basis } or null. basis is 'measured' (Path A: both
// distance and duration present) or 'assumed' (Paths B/C: one missing, the
// effort's assumed speed fills the gap). Path A may additionally carry
// `suspect: true` — see above; the flag is absent, not false, everywhere else.
//
// Null when weightLbs is missing, effort is missing or unrecognized, or
// neither distance nor duration is usable — a burn computed from a guessed
// weight or an unknown pace would be indistinguishable on screen from a
// measured one.
export function walkBurnEstimate({ distanceMiles, durationSeconds, effort, weightLbs }) {
  if (typeof weightLbs !== 'number' || !Number.isFinite(weightLbs) || weightLbs <= 0) return null;
  const speeds = ASSUMED_SPEEDS[effort];
  const grade = GRADE_MULTIPLIERS[effort];
  if (!speeds || grade === undefined) return null;

  const hasDistance = typeof distanceMiles === 'number' && Number.isFinite(distanceMiles) && distanceMiles > 0;
  const hasDuration = typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0;
  if (!hasDistance && !hasDuration) return null;

  const kg = weightLbs / LBS_PER_KG;
  const round = cal => Math.round(cal / WALK_BURN_ROUNDING) * WALK_BURN_ROUNDING;

  if (hasDistance && hasDuration) {
    // PATH A — speed is measured, so `effort` is no longer telling us about
    // pace — the pace is on the row. It is telling us about grade and load
    // (hills, a pack, a hard incline at a modest mph), which the flat-ground
    // speed curve cannot see. So the curve reads the measured speed and the
    // grade multiplier applies ON TOP — once, here, and never on the assumed
    // paths, where effort has already spent its information on the speed.
    const speed = distanceMiles / (durationSeconds / 3600);
    const suspect = speed < SUSPECT_LOW_MPH || speed > SUSPECT_HIGH_MPH;
    const minutes = durationSeconds / 60;
    const mid = netWalkCal(minutes, metForSpeed(speed) * grade, kg);
    const result = {
      low: round(mid * 0.9),
      mid: round(mid),
      high: round(mid * 1.1),
      basis: 'measured',
    };
    if (suspect) result.suspect = true;
    return result;
  }

  // PATHS B and C — one measurement is missing and the effort's assumed speed
  // stands in for it. The grade multiplier does NOT apply on either: effort
  // already determined the speed, and multiplying again would count the same
  // claim twice. Both bands come from recomputing at the effort's low and
  // high speeds rather than scaling the mid — the honest spread of the
  // assumption actually made. But the two paths spread differently, because
  // they hold different things fixed:
  //
  // Path B (duration only): the minutes are measured and stay fixed; the
  // speed assumption moves the MET alone, and the whole spread lands on the
  // calories.
  //
  // Path C (distance only): the distance is what is fixed, so the speed
  // assumption moves BOTH factors, in opposite directions — a slower walk
  // takes proportionally longer at a lower MET, a faster one is shorter at a
  // higher MET. The effects partially cancel, so C's band comes out TIGHTER
  // than B's for an equivalent walk: a distance-only entry is better
  // constrained than a duration-only one, the opposite of what intuition
  // suggests.
  //
  // One shape to know about. The MET curve is piecewise linear and its slope
  // jumps from 0.4 MET/mph (the 2.0–2.5 segment) to 1.0 MET/mph (the
  // 2.5–3.0 segment) at exactly 2.5 mph — which is easy's nominal speed.
  // Net-cal-vs-speed at fixed distance is therefore minimized precisely at
  // that kink: BOTH easy endpoints exceed the nominal and the band is
  // one-sided. This is an artifact of where the anchors and the assumed
  // speeds happen to coincide, not deep physics. Easy is the only effort
  // affected: steady's 3.3 sits mid-segment, and while hard's 4.0 is also an
  // anchor, the slope jump there (1.4 → 4.0 MET/mph) steepens a curve that
  // is already rising through it, so no minimum forms. The resolution is to
  // SORT all three results into low/high rather than retune either constant —
  // moving easy's nominal off 2.5 would hide the shape, not represent it.
  // mid stays the nominal result and may legitimately equal low or high: a
  // one-sided band is correct output, not a bug.
  if (hasDuration) {
    const minutes = durationSeconds / 60;
    const atSpeed = mph => netWalkCal(minutes, metForSpeed(mph), kg);
    return {
      low: round(atSpeed(speeds.low)),
      mid: round(atSpeed(speeds.nominal)),
      high: round(atSpeed(speeds.high)),
      basis: 'assumed',
    };
  }
  const atSpeed = mph => netWalkCal((distanceMiles / mph) * 60, metForSpeed(mph), kg);
  const slowEnd = atSpeed(speeds.low);
  const nominal = atSpeed(speeds.nominal);
  const fastEnd = atSpeed(speeds.high);
  return {
    low: round(Math.min(slowEnd, nominal, fastEnd)),
    mid: round(nominal),
    high: round(Math.max(slowEnd, nominal, fastEnd)),
    basis: 'assumed',
  };
}
